import React from 'react';

import { TFunction, useTranslation } from 'react-i18next';
import { Label } from 'semantic-ui-react';

import {
  DaysOfWeek,
  TariffComponentRestrictions,
} from 'subdomains/cpo/types/charging-policy';
import { TimeUnit } from 'subdomains/cpo/types/utility';
import { formatDateForDisplay } from 'subdomains/cpo/utils/date';
import { convertSecondsToHHMM } from 'utils/date';

type Props = {
  restrictions?: TariffComponentRestrictions;
  isParking?: boolean;
};

export const RestrictionLabels = ({ restrictions, isParking }: Props) => {
  const { t } = useTranslation();

  if (!restrictions) {
    return null;
  }

  const timeOfDayString = formatTimeOfDayRestriction(restrictions.timeOfDay, t);
  const durationString = formatDurationRestriction(restrictions.duration, t);
  const dateString = formatDateRestriction(restrictions.date);
  const energyString = formatEnergyRestriction(restrictions.energy, t);
  const daysOfWeekString = formatDaysOfWeekRestriction(
    restrictions.daysOfWeek,
    t
  );

  return (
    <>
      {timeOfDayString && (
        <Label basic>
          {t('tariff.restrictionLabel.timeOfDay', 'Time of day')}:{' '}
          {timeOfDayString}
        </Label>
      )}
      {durationString && (
        <Label basic>
          {isParking
            ? t('tariff.restrictionLabel.idleDuration', 'Idle duration')
            : t('tariff.restrictionLabel.duration', 'Duration')}
          : {durationString}
        </Label>
      )}
      {daysOfWeekString && (
        <Label basic>
          {t('tariff.restrictionLabel.daysOfWeek', 'Days of week')}:{' '}
          {daysOfWeekString}
        </Label>
      )}
      {dateString && (
        <Label basic>
          {t('tariff.restrictionLabel.date', 'Date')}: {dateString}
        </Label>
      )}
      {energyString && (
        <Label basic>
          {t('tariff.restrictionLabel.energy', 'Energy')}: {energyString}
        </Label>
      )}
    </>
  );
};

function formatTimeOfDayRestriction(
  timeOfDay: NonNullable<TariffComponentRestrictions>['timeOfDay'],
  t: TFunction<'translation', undefined>
): string {
  if (!timeOfDay?.enabled || (!timeOfDay.start && !timeOfDay.end)) return '';

  if (timeOfDay.start && timeOfDay.end) {
    return `${convertSecondsToHHMM(timeOfDay.start)}-${convertSecondsToHHMM(timeOfDay.end)}`;
  } else if (timeOfDay.start) {
    return t('tariff.restrictionLabel.previewTimeOfDayFrom', 'From {{start}}', {
      start: convertSecondsToHHMM(timeOfDay.start),
    });
  } else if (timeOfDay.end) {
    return t('tariff.restrictionLabel.previewTimeOfDayUntil', 'Until {{end}}', {
      end: convertSecondsToHHMM(timeOfDay.end),
    });
  }
  return '';
}

function formatDurationRestriction(
  duration: NonNullable<TariffComponentRestrictions>['duration'],
  t: TFunction<'translation', undefined>
): string {
  if (!duration?.enabled || (!duration.min && !duration.max)) return '';
  const labelHour = t('tariff.restrictionLabel.hour', 'h');
  const labelMinute = t('tariff.restrictionLabel.minute', 'min');

  const unit = duration.unit;
  const minUnit = unit === TimeUnit.HOUR ? labelHour : labelMinute;
  const maxUnit = unit === TimeUnit.HOUR ? labelHour : labelMinute;

  if (duration.min && duration.max) {
    return t(
      'tariff.restrictionLabel.previewDurationRange',
      '{{min}}{{minUnit}}-{{max}}{{maxUnit}}',
      {
        min: duration.min,
        max: duration.max,
        minUnit,
        maxUnit,
      }
    );
  } else if (duration.min) {
    return t('tariff.restrictionLabel.previewDurationMin', '>{{min}}{{unit}}', {
      min: duration.min,
      unit: minUnit,
    });
  } else if (duration.max) {
    return t('tariff.restrictionLabel.previewDurationMax', '<{{max}}{{unit}}', {
      max: duration.max,
      unit: maxUnit,
    });
  }
  return '';
}

function formatDaysOfWeekRestriction(
  daysOfWeek: NonNullable<TariffComponentRestrictions>['daysOfWeek'],
  t: TFunction<'translation', undefined>
): string {
  if (!daysOfWeek?.enabled || daysOfWeek.values.length === 0) return '';

  const dayAbbreviations: { [key: string]: string } = {
    [DaysOfWeek.MONDAY]: t(
      'tariff.restrictionLabel.previewDaysOfWeekMonday',
      'Mon'
    ),
    [DaysOfWeek.TUESDAY]: t(
      'tariff.restrictionLabel.previewDaysOfWeekTuesday',
      'Tue'
    ),
    [DaysOfWeek.WEDNESDAY]: t(
      'tariff.restrictionLabel.previewDaysOfWeekWednesday',
      'Wed'
    ),
    [DaysOfWeek.THURSDAY]: t(
      'tariff.restrictionLabel.previewDaysOfWeekThursday',
      'Thu'
    ),
    [DaysOfWeek.FRIDAY]: t(
      'tariff.restrictionLabel.previewDaysOfWeekFriday',
      'Fri'
    ),
    [DaysOfWeek.SATURDAY]: t(
      'tariff.restrictionLabel.previewDaysOfWeekSaturday',
      'Sat'
    ),
    [DaysOfWeek.SUNDAY]: t(
      'tariff.restrictionLabel.previewDaysOfWeekSunday',
      'Sun'
    ),
  };
  return daysOfWeek.values
    .map((day: string) => dayAbbreviations[day])
    .join(',');
}

function formatDateRestriction(
  date: NonNullable<TariffComponentRestrictions>['date']
): string {
  if (!date?.enabled || (!date.start && !date.end)) return '';

  if (date.start && date.end) {
    const start = new Date(date.start);
    const end = new Date(date.end);
    return `${formatDateForDisplay(start)}-${formatDateForDisplay(end)}`;
  } else if (date.start) {
    const start = new Date(date.start);
    return `From ${formatDateForDisplay(start)}`;
  } else if (date.end) {
    const end = new Date(date.end);
    return `Until ${formatDateForDisplay(end)}`;
  }
  return '';
}

function formatEnergyRestriction(
  energy: NonNullable<TariffComponentRestrictions>['energy'],
  t: TFunction<'translation', undefined>
): string {
  if (!energy?.enabled || (!energy.min && !energy.max)) return '';

  if (energy.min && energy.max) {
    return t(
      'tariff.restrictionLabel.previewEnergyRange',
      '{{min}}-{{max}}kWh',
      {
        min: energy.min,
        max: energy.max,
      }
    );
  } else if (energy.min) {
    return t('tariff.restrictionLabel.previewEnergyMin', '>{{min}}kWh', {
      min: energy.min,
    });
  } else if (energy.max) {
    return t('tariff.restrictionLabel.previewEnergyMax', '<{{max}}kWh', {
      max: energy.max,
    });
  }
  return '';
}
